<template>
  <div>
    <Head :title="stockOut.code" />
    <h1 class="mb-8 text-2xl font-bold">
      Transaksi
      <span class="text-indigo-400 font-medium"> / </span>
      <Link class="text-indigo-400 hover:text-indigo-600" href="/stockouts">
        Pengeluaran Barang
      </Link>
      <span class="text-indigo-400 font-medium"> / </span>
      {{ stockOut.code }}
    </h1>
    <div class="max-w-3xl bg-white rounded-md shadow overflow-hidden">
      <form @submit.prevent="update">
        <div class="flex flex-wrap -mb-8 -mr-6 p-8">
          <div class="flex flex-col pb-8 pr-6 w-full md:w-1/2">
            <template v-if="isInputEnabled">
              <label for="trn_date">Tanggal:</label>
              <flat-pickr
                v-model="form.trn_date"
                :error="form.errors.trn_date"
                class="mt-2 p-2 w-full border rounded"
                name="recv_date" />
              <div v-if="form.errors.trn_date" class="form-error">
                {{ form.errors.trn_date }}
              </div>
            </template>
            <template v-else>
              <text-input
                :model-value="formatDate(form.trn_date)"
                class="w-full md:w-1/2"
                label="Tanggal Keluar"
                disabled />
            </template>
          </div>
          <select-input
            v-model="form.purchase_request_id"
            :error="form.errors.purchase_request_id"
            class="pb-8 pr-6 w-full lg:w-1/2"
            label="Purchase Request"
            :disabled="isDetailsFound">
            <option
              v-for="purchaseReq in purchaseReqs"
              :key="purchaseReq.id"
              :value="purchaseReq.id">
              {{ purchaseReq.code }}
            </option>
          </select-input>
          <text-input
            v-model="form.activity_name"
            :error="form.errors.activity_name"
            class="pb-8 pr-6 w-full lg:w-1/2"
            label="Nama Aktivitas"
            :disabled="!isInputEnabled" />
          <select-input
            v-model="form.item_location_id"
            :error="form.errors.item_location_id"
            class="pb-8 pr-6 w-full lg:w-1/2"
            label="Lokasi Item"
            :disabled="!isInputEnabled">
            <option
              v-for="itemLocation in itemLocations"
              :key="itemLocation.id"
              :value="itemLocation.id">
              {{ itemLocation.name }}
            </option>
          </select-input>
          <textarea-input
            v-model="form.desc"
            :error="form.errors.desc"
            class="pb-8 pr-6 w-full lg:w-1/2"
            label="Description"
            :disabled="!isInputEnabled" />
          <textarea-input
            v-model="form.kepada"
            :error="form.errors.kepada"
            class="pb-8 pr-6 w-full lg:w-1/2"
            label="Kepada"
            :disabled="!isInputEnabled" />
          <select-input
            v-model="form.signature01_id"
            :error="form.errors.signature01_id"
            class="pb-8 pr-6 w-1/2 md:w-1/3"
            label="Mengetahui"
            :disabled="!isInputEnabled">
            <option
              v-for="employee in employees"
              :key="employee.id"
              :value="employee.id">
              {{ employee.name }}
            </option>
          </select-input>
          <text-input
            v-model="form.signature02_id"
            :error="form.errors.signature02_id"
            class="pb-8 pr-6 w-full md:w-1/3"
            label="Menerima"
            :disabled="!isInputEnabled" />
          <text-input
            v-model="form.signature03_id"
            :error="form.errors.signature03_id"
            class="pb-8 pr-6 w-full md:w-1/3"
            label="Mengeluarkan"
            :disabled="!isInputEnabled" />
        </div>

        <div class="-mr-6 px-8 py-4 border-t border-gray-100">
          <h3 class="text-xl" id="attachments">
            List berkas pendukung:
          </h3>

          <ul
            v-for="(docfile, idx) in stockOut.files"
            :key="idx"
            class="w-full list-disc">
            <li class="flex gap-2 items-center">
              <span>- </span>
              <a
                :href="docfile.url"
                :class="attachmentAccess.download ? 'text-indigo-500 underline' : 'text-red-600'"
                target="_blank"
                :download="docfile.file_name"
                :disable="!attachmentAccess.download"> {{ docfile.file_name }}</a> ( {{ docfile.size }} )
              <a
                href=""
                title="Delete attachment file"
                @click.prevent="destroyAttachment(docfile.uuid)">
                <Icon icon="ic:baseline-delete-forever" class="w-6 h-6 text-red-600" />
              </a>
            </li>
          </ul>

          <file-input
            v-model="form.attachment"
            :error="form.errors.attachment"
            class="pr-6 py-4 w-full md:w-2/3"
            type="file"
            accept="image/*, application/pdf"
            label="Browse dan upload file dokumen pendukung (Max 10MB)"
            @update:model-value="uploadAttachment"
            v-if="attachmentAccess.upload" />
        </div>

        <div class="flex items-center px-8 py-4 bg-gray-50 border-t border-gray-100">
          <template v-if="isInputEnabled">
            <button
              class="btn-red ml-auto"
              type="button"
              @click.prevent="confirmPengeluaran">
              <span>Confirm</span>
              <span class="hidden md:inline">&nbsp;Pengeluaran</span>
            </button>
            <loading-button
              :loading="form.processing"
              class="btn-indigo ml-2"
              type="submit">
              <span>Update</span>
              <span class="hidden md:inline">&nbsp;Pengeluaran</span>
            </loading-button>
          </template>
          <template v-else>
            <a
              :href="`/stockouts/${stockOut.id}/print`"
              as="button"
              target="_blank"
              class="btn-green ml-auto"> Print Stock Keluar (PDF) </a>
          </template>
        </div>
      </form>
    </div>
    <h2 class="mt-12 text-2xl font-bold">
      Details
    </h2>
    <div class="mt-6 bg-white rounded shadow overflow-x-auto">
      <table class="whitespace-wrap w-full">
        <thead>
          <tr class="font-bold">
            <th class="align-middle px-2 text-center border-r">
              No.
            </th>
            <th class="px-6 py-4 w-5/12 text-left">
              Item
            </th>
            <th class="px-6 py-4 w-2/12 text-left">
              Unit
            </th>
            <th class="px-6 py-4 w-1/12 text-right">
              Quantity
            </th>
            <th class="px-6 py-4 w-4/12 text-left">
              Description
            </th>
          </tr>
        </thead>
        <tbody>
          <detail
            v-for="(stockOutDetail, idx) in stockOutDetails.data"
            :key="stockOutDetail.id"
            :editable-mode="stockOutDetail.editable"
            :stockoutdetail="stockOutDetail"
            :items="items"
            :errors="$page.props.errors"
            :row-index="++idx"
            :disabled="!isInputEnabled"
            :current-page="stockOutDetails.current_page"
            :per-page="stockOutDetails.per_page"
            @destroy:detail="destroyDetail"
            @edit:detail="editDetail"
            @cancel="cancel('edit')"
            @save="updateDetail" />
          <tr v-if="!isDetailsFound">
            <td class="px-6 py-4 border-t" colspan="5">
              No details found.
            </td>
          </tr>
        </tbody>
      </table>
    </div>
    <pagination class="mt-6" :links="stockOutDetails.links" />
  </div>
</template>

<script lang="js">
import { Head, Link } from '@inertiajs/vue3'
import { format, parse } from 'date-fns'
import { Icon } from '@iconify/vue'
import find from 'lodash/find'
import Layout from '@/Shared/Layout'
import TextInput from '@/Shared/TextInput'
import SelectInput from '@/Shared/SelectInput'
import LoadingButton from '@/Shared/LoadingButton'
import TextareaInput from '@/Shared/TextareaInput'
import flatPickr from 'vue-flatpickr-component'
import 'flatpickr/dist/flatpickr.css'
import Detail from '@/Pages/StockOuts/DetailRow'
import Pagination from '@/Shared/Pagination'
import FileInput from '@/Shared/FileInput'

export default {
    components: {
        Head,
        Link,
        Icon,
        LoadingButton,
        SelectInput,
        TextInput,
        TextareaInput,
        flatPickr,
        Pagination,
        Detail,
        FileInput,
    },
    layout: Layout,
    props: {
        stockOut: Object,
        stockOutDetails: Object,
        purchaseReqs: Object,
        itemLocations: Object,
        items: Object,
        units: Object,
        employees: Object,
        attachmentAccess: Object,
    },
    remember: 'form',
    data () {
        return {
            form: this.$inertia.form({
                id: this.stockOut.id,
                trn_date: this.stockOut.trn_date,
                purchase_request_id: this.stockOut.purchase_request_id,
                activity_name: this.stockOut.activity_name,
                item_location_id: this.stockOut.item_location_id,
                desc: this.stockOut.desc,
                kepada: this.stockOut.kepada,
                signature01_id: this.stockOut.signature01_id,
                signature02_id: this.stockOut.signature02_id,
                signature03_id: this.stockOut.signature03_id,
                editable: this.stockOut.editable,
                attachment: null,
            }),
            createMode: false,
            editedItem: {
                id: null,
                item_id: null,
                item_unit_id: null,
                desc: null,
                qty_out: null,
                editable: false,
            },
            detailKeyRefresh: 0,
        }
    },
    computed: {
        isDetailsFound () {
            return this.stockOutDetails.data.length > 0
        },
        isInputEnabled () {
            return this.stockOut.editable
        },
    },
    methods: {
        update () {
            this.form.post(`/stockouts/${this.stockOut.id}`, {
                preserveScroll: true,
                onSuccess: () => this.form.reset('attachment'),
            })
        },
        destroy () {
            if (confirm('Are you sure you want to delete this purchase request?')) {
                this.$inertia.delete(`/stockouts/${this.stockOut.id}`)
            }
        },
        uploadAttachment () {
            this.form.attachment && this.update()
        },
        destroyAttachment (uuid) {
            if (confirm('Are you sure you want to delete this attachment file?')) {
                this.$inertia.delete(`/attachment/${uuid}`)
            }
        },
        restore () {
            if (confirm('Are you sure you want to restore this purchase request?')) {
                this.$inertia.put(`/stockouts/${this.stockOut.id}/restore`)
            }
        },
        formatDate (ymd) {
            return format(parse(ymd, 'yyyy-MM-dd', new Date()), 'dd-MMM-yyyy')
        },
        formatNumber (num) {
            return num ? num.toLocaleString('id-ID') : '0'
        },
        destroyDetail (detailId) {
            this.$inertia.delete(`/stockoutsdetails/${detailId}`)
        },
        resetEditedItem () {
            this.editedItem = {
                id: null,
                item_id: null,
                item_unit_id: null,
                desc: null,
                qty_out: null,
                editable: false,
            }
        },
        cancel (action) {
            if(action === 'create')
                this.resetEditedItem()
            if (action === 'edit')
                this.$inertia.reload({ only: ['stockOutDetails']})
        },
        createDetail (value) {
            const formDetail = this.$inertia.form({
                stocks_out_m_id: this.stockOut.id,
                item_id: value.item.id,
                item_unit_id: value.item_unit.id,
                desc: value.desc,
                qty_out: value.qty_out,
            })
            formDetail.post('/stockoutsdetails', {
                preserveScroll: true,
                onSuccess: () => {
                    this.cancel('create')
                },
                onError: () => {
                    this.editedItem = value
                },
            })
        },
        editDetail (detail) {
            let edited = find(this.stockOutDetails.data, ['id', detail.id])
            edited.editable = true
        },
        updateDetail (value) {
            const formDetail = this.$inertia.form({
                item_id: value.item.id,
                qty_out: value.qty_out,
                item_unit_id: value.item_unit.id,
                desc: value.desc,
            })
            formDetail.put(`/stockoutsdetails/${value.id}`, {
                preserveScroll: true,
                onError: () => {
                    this.editedItem = value
                },
            })
        },
        confirmPengeluaran () {
            if (confirm('Informasi pengeluaran stok barang dan detailnya tidak bisa diubah lagi setelah proses ini, konfirmasikan?'))
            {
                this.form.editable = false
                this.update()
            }
        },
    },
}
</script>
